import { LockClosedIcon } from '@heroicons/react/solid'
import { useAppContext } from 'context/AppContext'
import { AuthContext, useAuth, default_primary_color } from 'context/AuthContext'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { CognitoErrorTypes } from 'types/cognito'
import { classNames } from 'utils/dom-helpers'
import { createModal } from '../ModalFactory'
import { ModalWrapper } from '../ModalWrapper'
import { SubmitError } from '../types'
import Translation from 'components/Translation'
import { useTenantContext } from 'context/TenantContext'
import Cookies from 'js-cookie'
import { FormValues, StyledMaskedTextBox, VIEW_MODES } from './types'
import changeTemporaryPassword from 'services/cognito/respond-to-auth-challenge'
import InputSpinner from 'components/Spinners/input'
import resendVerification from 'services/cognito/resend-verification'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { EMAIL_REGEX } from 'types/common'
import InputValidations from 'components/Validations/Field'
import getConfig from 'next/config'

const ModalProvider = createModal(AuthContext, 'ClientLoginModal', () => (
    <>Login</>
))

export const ClientLoginModalOpener = ModalProvider.Opener
export const ClientLoginModalCloser = ModalProvider.Closer

function ClientLoginModal() {
    const router = useRouter()
    const ctx = useAuth()
    const { tenant, locationBg, primaryColor } = useTenantContext()
    const { setGlobalError } = useAppErrorApiContext()
    const { translations, loadComponentTranslations, setGlobalLoading } = useAppContext()
    const [loading, toggleLoading] = useState(false)
    const [view_mode, setViewMode] = useState<VIEW_MODES>(VIEW_MODES.LOGIN)
    const [resend_email, setResendEmail] = useState('')
    const [Session, setSession] = useState<string>()
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        reset,
    } = useForm<FormValues>({
        mode: 'onSubmit',
    })

    useEffect(() => {
        if (!translations.LOGIN_TRANSLATION_ID) {
            loadComponentTranslations('LOGIN_TRANSLATION_ID')
        }
    }, [translations])

    useEffect(() => {
        if (!ctx.ClientLoginModal.is_open && !Cookies.get("id_token") && router.pathname.includes("/book/client/")) {
            router.push({
                pathname: "/book/",
                query: router.query
            })
        }
    }, [ctx.ClientLoginModal.is_open])

    const onSubmitRequestReset: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        toggleLoading(true)
        try {
            const res = await ctx.forgotPassword(values.email)
            setViewMode(VIEW_MODES.NEW_PASSWORD)
        } catch (e) {
            const { name, message } = e as SubmitError
            setError('email', {
                type: name,
                message,
            })
        }
        toggleLoading(false)
    }

    const onSubmitNewPassword: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        toggleLoading(true)
        try {
            await ctx.confirmForgotPassword(
                values.email,
                values.new_password,
                values.code
            )
            reset()
            setViewMode(VIEW_MODES.LOGIN)
        } catch (e) {
            const { name, message } = e as SubmitError
            if (name == CognitoErrorTypes.InvalidParameterException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidParameterException,
                    message,
                })
            } else if (name == CognitoErrorTypes.InvalidPasswordException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidPasswordException,
                    message,
                })
            } else {
                setError('email', { message })
            }
        }
        toggleLoading(false)
    }

    const onSubmitNewPasswordWithChallenge: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        toggleLoading(true)
        try {
            await changeTemporaryPassword(
                values,
                Session
            )
            reset()
            setViewMode(VIEW_MODES.LOGIN)
        } catch (e) {
            const { name, message } = e as SubmitError
            if (name == CognitoErrorTypes.InvalidParameterException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidParameterException,
                    message,
                })
            } else if (name == CognitoErrorTypes.InvalidPasswordException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidPasswordException,
                    message,
                })
            } else {
                setError('email', { message })
            }
        }
        toggleLoading(false)
    }

    const onSubmit: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        try {
            toggleLoading(true)
            const { email, password } = values
            const res = await ctx.login(email, password)
            const { AuthenticationResult, ChallengeName, Session } = res
            if (AuthenticationResult) {
                reset()
                ctx.ClientLoginModal.close()
                router.push('/book/client/appointments')
            }
            else if (ChallengeName && Session) {
                setSession(Session)
                setViewMode(VIEW_MODES.REQUIRE_NEW_PASSWORD)
            }
        } catch (e) {
            const { name, message } = e as SubmitError
            if (name == CognitoErrorTypes.NotAuthorizedException) {
                setError('email', {
                    type: CognitoErrorTypes.NotAuthorizedException,
                    message,
                })
                setError('password', {
                    type: CognitoErrorTypes.NotAuthorizedException,
                    message,
                })
            }
            if (name == CognitoErrorTypes.UserNotConfirmedException) {
                setError('email', {
                    type: CognitoErrorTypes.UserNotConfirmedException,
                    message,
                })
                setError('password', {
                    type: CognitoErrorTypes.UserNotConfirmedException,
                    message,
                })
                setResendEmail(values.email)
            }
        } finally {
            toggleLoading(false)
        }
    }

    const onResendEmail = async () => {
        try {
            setGlobalLoading(true)

            await resendVerification({ email: resend_email })

            ctx.EmailVerificationModal.setAttributes({ email: resend_email })
            ctx.EmailVerificationModal.open()
        } catch (err) {
            setGlobalError(err.error)
        } finally {
            setGlobalLoading(false)
            setResendEmail('')
        }
    }

    const getModalCloser = () => (
        <div
            onClick={() => {
                ctx.ClientLoginModal.close()
            }}
            className="absolute right-8 top-8"
        >
            <ClientLoginModalCloser className="self-center close-login" />
        </div>
    )

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="w-full p-3">
                <div className="shadow-3xl bg-white rounded-xl 
                    w-full sm:w-full md:w-full lg:w-2/3 xl:w-2/3 2xl:w-wider 
                    h-auto sm:h-4/5 lg:h-auto 2xl:h-auto 
                    m-auto relative flex p-0">
                    <div className="w-1/2 h-full hidden sm:hidden  md:hidden lg:hidden xl:block 2xl:block">
                        <div style={{ backgroundImage: `url(${encodeURI(locationBg || `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/booking_background.jpg`)})` }}
                            className="h-full bg-no-repeat w-full bg-cover bg-center">
                            <div className="flex justify-center flex-col  items-center h-1/3">
                                {/* Company Logo here */}
                            </div>
                        </div>
                    </div>
                    <div className="w-full sm:w-full md:w-full lg:w-full xl:w-1/2 2xl:w-1/2 py-8 px-10">
                        {getModalCloser()}
                        <div className="flex flex-col justify-between h-full">
                            <div className="flex-shrink-0 flex items-center mb-10">
                                <div className="block sm:block lg:block h-12 w-auto">
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <div className="flex justify-between  text-gray-500   w-full">
                                    {view_mode == VIEW_MODES.LOGIN ? (
                                        <Translation
                                            content_key="booking.login_to"
                                            content_values={{
                                                tenant_name: tenant?.name as string
                                            }}
                                            render_as="span"
                                            className="inline-block self-center text-lg font-bold text-gray-600 text-center"
                                        />
                                    ) : (
                                        <button
                                            className="text-primary"
                                            onClick={() => {
                                                setViewMode(VIEW_MODES.LOGIN)
                                            }}
                                        >
                                            <span>&larr; </span>
                                            <Translation
                                                content_key="booking.back_to_login"
                                                render_as="span"
                                                className="underline"
                                            />
                                        </button>
                                    )}
                                </div>
                                <form
                                    method="POST"
                                    onSubmit={handleSubmit(onSubmit)}
                                    className="z-20 pt-10 "
                                >
                                    <fieldset className="pb-6">
                                        <label
                                            htmlFor="email-address"
                                            className={classNames(
                                                'block font-bold text-gray-600',
                                                errors.email?.type
                                                    ? 'text-red-700'
                                                    : ''
                                            )}
                                        >
                                            <Translation content_key="booking.email_address_label" />
                                            <span className='text-red-500'> *</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="email-address"
                                            autoComplete="email"
                                            readOnly={view_mode == VIEW_MODES.NEW_PASSWORD}
                                            placeholder={
                                                translations['booking.enter_email']
                                            }
                                            className={classNames(
                                                'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                                errors.email?.type
                                                    ? 'border-red-300 bg-red-100'
                                                    : '',
                                                'h-12'
                                            )}
                                            {...register('email', {
                                                required: true,
                                                pattern: EMAIL_REGEX
                                            })}
                                        />
                                        <InputValidations
                                            field_error={errors.email}
                                            required_key='booking.email_required'
                                            pattern_key='booking.valid_email_required'
                                        />
                                    </fieldset>

                                    {view_mode == VIEW_MODES.LOGIN ? (
                                        <>
                                            <fieldset className="pb-6">
                                                <label
                                                    htmlFor="password"
                                                    className={classNames(
                                                        'block font-bold text-gray-600',
                                                        errors.password?.type
                                                            ? 'text-red-700'
                                                            : ''
                                                    )}
                                                >
                                                    <Translation content_key="booking.password_label" />
                                                    <span className='text-red-500'> *</span>
                                                </label>
                                                <input
                                                    type="password"
                                                    id="password"
                                                    autoComplete="email"
                                                    placeholder={
                                                        translations[
                                                        'booking.enter_password'
                                                        ]
                                                    }
                                                    className={classNames(
                                                        'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                                        errors.password?.type
                                                            ? 'border-red-300 bg-red-100'
                                                            : '',
                                                        'h-12'
                                                    )}
                                                    {...register('password', {
                                                        required: true,
                                                        minLength: 8,
                                                    })}
                                                />
                                                <InputValidations
                                                    field_error={errors.password}
                                                    required_key='booking.password_required'
                                                    minlength_key='booking.valid_password_required'
                                                />
                                            </fieldset>

                                            <button
                                                type="submit"
                                                className={classNames(
                                                    'group relative w-full flex justify-center',
                                                    'py-2 mt-4 px-4 border border-transparent',
                                                    'text-md rounded-md text-white',
                                                    !loading
                                                        ? 'focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                                        : '',
                                                    'h-12 flex items-center'
                                                )}
                                                style={{
                                                    backgroundColor: loading
                                                        ? `${primaryColor || default_primary_color}50`
                                                        : primaryColor || default_primary_color,
                                                }}
                                                onClick={handleSubmit(onSubmit)}
                                                disabled={loading}
                                            >
                                                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                                    <InputSpinner loading={loading} />
                                                </span>
                                                <Translation content_key="booking.login_button" />
                                            </button>
                                        </>
                                    ) : ''}

                                    {view_mode == VIEW_MODES.FORGET_PASSWORD ? (
                                        <>
                                            <button
                                                type="submit"
                                                className={classNames(
                                                    'group relative w-full flex justify-center',
                                                    'py-2 mt-4 px-4 border border-transparent',
                                                    'text-md rounded-md text-white',
                                                    'focus:outline-none',
                                                    loading
                                                        ? 'bg-primary-light'
                                                        : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                                    'h-12 flex items-center'
                                                )}
                                                onClick={handleSubmit(
                                                    onSubmitRequestReset
                                                )}
                                            >
                                                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                                    {loading ? (
                                                        <InputSpinner />
                                                    ) : (
                                                        <LockClosedIcon
                                                            className="h-5 w-5 text-white"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </span>
                                                <Translation content_key="booking.send_reset_code_button" />
                                            </button>
                                        </>
                                    ) : ''}

                                    {view_mode == VIEW_MODES.NEW_PASSWORD ? (
                                        <>
                                            <fieldset className="pb-6">
                                                <label
                                                    htmlFor="new_password"
                                                    className={classNames(
                                                        'block font-bold text-gray-600',
                                                        errors.new_password
                                                            ?.type
                                                            ? 'text-red-700'
                                                            : ''
                                                    )}
                                                >
                                                    <Translation content_key='booking.login_new_password' />
                                                </label>
                                                <input
                                                    type="password"
                                                    id="new_password"
                                                    autoComplete="email"
                                                    className={classNames(
                                                        'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                                        errors.new_password
                                                            ?.type
                                                            ? 'border-red-300 bg-red-100'
                                                            : ''
                                                    )}
                                                    {...register(
                                                        'new_password',
                                                        {
                                                            required: true,
                                                            minLength: 8,
                                                        }
                                                    )}
                                                />
                                                <InputValidations
                                                    field_error={errors.new_password}
                                                    required_key='booking.login_password_required'
                                                    minlength_key='booking.login_valid_password_required'
                                                />
                                            </fieldset>

                                            <fieldset className="pb-6">
                                                <label
                                                    htmlFor="code"
                                                    className={classNames(
                                                        'block font-bold text-gray-600',
                                                        errors.code?.type
                                                            ? 'text-red-700'
                                                            : ''
                                                    )}
                                                >
                                                    <Translation content_key='booking.login_code' />
                                                </label>

                                                <StyledMaskedTextBox
                                                    className={classNames(
                                                        'mt-1 focus:ring-primary-light focus:border-primary-light block w-full sm:text-sm border-gray-300 rounded-md',
                                                        errors.code?.type
                                                            ? 'border-red-300 bg-red-100'
                                                            : ''
                                                    )}
                                                    id="code"
                                                    mask="000000"
                                                    {...register('code', {
                                                        required: true,
                                                    })}
                                                />
                                                <InputValidations
                                                    field_error={errors.code}
                                                    required_key='booking.login_code_required'
                                                />
                                            </fieldset>

                                            <button
                                                type="submit"
                                                className={classNames(
                                                    'group relative w-full flex justify-center',
                                                    'py-2 mt-4 px-4 border border-transparent',
                                                    'text-md rounded-md text-white',
                                                    'focus:outline-none',
                                                    loading
                                                        ? 'bg-primary-light'
                                                        : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                                    'h-12 flex items-center'
                                                )}
                                                onClick={handleSubmit(
                                                    onSubmitNewPassword
                                                )}
                                            >
                                                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                                    {loading ? (
                                                        <InputSpinner />
                                                    ) : (
                                                        <LockClosedIcon
                                                            className="h-5 w-5 text-white"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </span>
                                                <Translation content_key='booking.update_acct_password' />
                                            </button>
                                        </>
                                    ) : ''}

                                    {view_mode == VIEW_MODES.REQUIRE_NEW_PASSWORD ? (
                                        <>
                                            <fieldset className="pb-6">
                                                <label
                                                    htmlFor="new_password"
                                                    className={classNames(
                                                        'block font-bold text-gray-600',
                                                        errors.new_password
                                                            ?.type
                                                            ? 'text-red-700'
                                                            : ''
                                                    )}
                                                >
                                                    <Translation content_key='booking.login_new_password' />
                                                </label>
                                                <input
                                                    type="password"
                                                    id="new_password"
                                                    autoComplete="email"
                                                    className={classNames(
                                                        'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                                        errors.new_password
                                                            ?.type
                                                            ? 'border-red-300 bg-red-100'
                                                            : ''
                                                    )}
                                                    {...register(
                                                        'new_password',
                                                        {
                                                            required: true,
                                                            minLength: 8,
                                                        }
                                                    )}
                                                />
                                                <InputValidations
                                                    field_error={errors.new_password}
                                                    required_key='booking.login_password_required'
                                                    minlength_key='booking.login_valid_password_required'
                                                />
                                            </fieldset>

                                            <button
                                                type="submit"
                                                className={classNames(
                                                    'group relative w-full flex justify-center',
                                                    'py-2 mt-4 px-4 border border-transparent',
                                                    'text-md rounded-md text-white',
                                                    'focus:outline-none',
                                                    loading
                                                        ? 'bg-primary-light'
                                                        : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                                    'h-12 flex items-center'
                                                )}
                                                onClick={handleSubmit(
                                                    onSubmitNewPasswordWithChallenge
                                                )}
                                            >
                                                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                                    {loading ? (
                                                        <InputSpinner />
                                                    ) : (
                                                        <LockClosedIcon
                                                            className="h-5 w-5 text-white"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </span>
                                                <Translation content_key='booking.update_acct_password' />
                                            </button>
                                        </>
                                    ) : (
                                        ''
                                    )}

                                    {Object.values(
                                        CognitoErrorTypes
                                    ).includes(
                                        errors.email
                                            ?.type as CognitoErrorTypes
                                    ) ? (
                                        <div className="bg-red-100 rounded px-3 py-1 mb-5  mt-12 md:mt-5">
                                            {errors.email?.message && (
                                                <span className="text-sm text-red-700">
                                                    <Translation content_key={errors.email?.message} />
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        ''
                                    )}
                                </form>

                                {view_mode == VIEW_MODES.LOGIN ? (
                                    <div className="z-20 flex justify-center md:justify-end">
                                        {resend_email ? (
                                            <button
                                                onClick={onResendEmail}
                                            >
                                                <Translation content_key="resend_email" />
                                            </button>
                                        ) : (
                                            <button
                                                className="mt-8"
                                                onClick={() =>
                                                    setViewMode(VIEW_MODES.FORGET_PASSWORD)
                                                }
                                            >
                                                <Translation content_key="booking.forgot_password" />
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                            <div className="flex flex-col items-center mb-10">
                                <div className="mt-11 text-center">
                                    <Translation content_key="booking.dont_have_account" />
                                    <button
                                        className="p-2 text-primary-light"
                                        onClick={() => {
                                            ctx.ClientLoginModal.close()
                                            ctx.ClientSignupModal.open()
                                        }}
                                    >
                                        <Translation content_key="sign_up_here" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default ClientLoginModal
